/* FILE: ./frontend/src/features/admin/components/AnalyticsSuite.jsx */
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

export default function AnalyticsSuite() {
  const [view, setView] = useState('macro'); 
  const [loading, setLoading] = useState(false);
  
  const [microData, setMicroData] = useState(null);
  const [mesoData, setMesoData] = useState(null);
  const [macroData, setMacroData] = useState(null);

  const demoSupplierId = '00000000-0000-0000-0000-000000000000'; 

  useEffect(() => {
    fetchData(view);
  }, [view]);

  const fetchData = async (currentView) => {
    setLoading(true);
    try {
      if (currentView === 'micro' && !microData) {
        // Hitting the secure ADMIN micro endpoint
        const res = await axios.get(`${API_BASE}/analytics/admin/micro/${demoSupplierId}`);
        setMicroData(res.data);
      } else if (currentView === 'meso' && !mesoData) {
        // Hitting the secure ADMIN meso endpoint
        const res = await axios.get(`${API_BASE}/analytics/admin/meso`);
        setMesoData(res.data);
      } else if (currentView === 'macro' && !macroData) {
        // Hitting the secure ADMIN macro endpoint
        const res = await axios.get(`${API_BASE}/analytics/admin/macro`);
        setMacroData(res.data);
      }
    } catch (err) {
      console.error(`Error fetching ${currentView} analytics:`, err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-full p-2">
      
      {/* ANALYTICS NAVIGATION */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-200">
        <div>
          <h2 className="text-xl font-black text-slate-800">Data & Monetization Suite</h2>
          <p className="text-sm text-slate-500">Live analytics connected to the TariffBeater engine.</p>
        </div>
        <div className="flex bg-slate-100 p-1 rounded-lg">
          <button onClick={() => setView('micro')} className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${view === 'micro' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>Micro (Supplier)</button>
          <button onClick={() => setView('meso')} className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${view === 'meso' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>Meso (Enterprise)</button>
          <button onClick={() => setView('macro')} className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${view === 'macro' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>Macro (3rd Party)</button>
        </div>
      </div>

      {loading && <div className="p-12 text-center text-slate-400 animate-pulse font-bold">Querying Database...</div>}

      {/* --- TIER 1: MICRO --- */}
      {view === 'micro' && microData && !loading && (
        <div className="space-y-6 animate-fade-in">
          <div className="bg-blue-900 text-white p-6 rounded-xl shadow-md flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold flex items-center gap-2"><span>📈</span> Supplier Intelligence Preview</h3>
              <p className="text-blue-200 text-sm mt-1">Included in the Premium Subscription. Drives upgrades.</p>
            </div>
            <span className="bg-blue-800 px-3 py-1 rounded text-xs font-mono border border-blue-700">Preview Mode</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Quote Conversion Rate</h4>
              <div className="flex items-end gap-3 mb-2">
                <span className="text-4xl font-black text-slate-800">{microData.winRate}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2.5 mt-4 flex overflow-hidden">
                <div className="bg-green-500 h-2.5" style={{ width: `${microData.winRate}%` }}></div>
                <div className="bg-slate-300 h-2.5" style={{ width: `${100 - microData.winRate}%` }}></div>
              </div>
              <div className="flex justify-between text-xs text-slate-500 mt-2">
                <span>Won: {microData.wonQuotes}</span>
                <span>Lost: {microData.lostQuotes}</span>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Market Price Positioning</h4>
              <div className="text-center py-2">
                <span className={`text-xs font-bold px-3 py-1 rounded-full ${microData.priceOffset > 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                  {Math.abs(microData.priceOffset)}% {microData.pricePosition}
                </span>
              </div>
              <div className="relative pt-6 pb-2">
                <div className="w-full bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 rounded-full h-3"></div>
                <div className="absolute top-4 w-1 h-7 bg-slate-900 border-2 border-white shadow-sm" style={{ left: `${50 + microData.priceOffset}%` }}></div>
                <div className="flex justify-between text-[10px] text-slate-400 mt-2 font-bold uppercase">
                  <span>Below Avg</span>
                  <span>Average</span>
                  <span>Above Avg</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Profile Discoverability</h4>
              <div className="flex items-end gap-3">
                <span className="text-4xl font-black text-slate-800">{microData.searchAppearances}</span>
                <span className="text-sm text-slate-500 mb-1">Search Appearances</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- TIER 2: MESO --- */}
      {view === 'meso' && mesoData && !loading && (
        <div className="space-y-6 animate-fade-in">
          <div className="bg-emerald-900 text-white p-6 rounded-xl shadow-md flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold flex items-center gap-2"><span>🏢</span> Enterprise Procurement Suite Preview</h3>
              <p className="text-emerald-200 text-sm mt-1">High-ticket SaaS add-on ($499/mo) for large buyers & governments.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              <div className="flex justify-between items-center mb-6">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Reciprocity Graph</h4>
                <select className="text-xs border border-slate-200 rounded p-1"><option>Ontario (Origin)</option></select>
              </div>
              <div className="space-y-4">
                {mesoData.reciprocity.map((prov, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className={`font-bold ${prov.match > 80 ? 'text-emerald-700' : 'text-yellow-700'}`}>{prov.province}</span>
                      <span className="text-slate-500">{prov.match}% Match</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div className={`${prov.match > 80 ? 'bg-emerald-500' : 'bg-yellow-500'} h-2 rounded-full`} style={{width: `${prov.match}%`}}></div>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1">{prov.note}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-6">Supplier Risk Monitoring</h4>
              <ul className="space-y-3">
                {mesoData.alerts.length === 0 ? (
                   <li className="text-sm text-gray-500 italic p-3 bg-gray-50 rounded">All network suppliers are currently fully compliant.</li>
                ) : (
                  mesoData.alerts.map((alert, idx) => (
                    <li key={idx} className="flex gap-3 items-start p-3 bg-red-50 border border-red-100 rounded-lg">
                      <span className="text-red-500 mt-0.5">⚠️</span>
                      <div>
                        <span className="text-sm font-bold text-red-900 block">{alert.title}</span>
                        <span className="text-xs text-red-700">{alert.description}</span>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* --- TIER 3: MACRO --- */}
      {view === 'macro' && macroData && !loading && (
        <div className="space-y-6 animate-fade-in">
          <div className="bg-indigo-900 text-white p-6 rounded-xl shadow-md flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold flex items-center gap-2"><span>🌍</span> Canadian Trade Flow API Preview</h3>
              <p className="text-indigo-200 text-sm mt-1">Licensed data ($10k+/yr) for Hedge Funds, Consulting Firms, and Govt.</p>
            </div>
            <span className="bg-indigo-800 px-3 py-1 rounded text-xs font-mono border border-indigo-700">Live Aggregation</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-6">Real-Time Demand Velocity (RFQ Volume Spikes)</h4>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-slate-400 border-b border-slate-100">
                    <th className="pb-2 text-left font-bold uppercase text-[10px]">HS Category</th>
                    <th className="pb-2 text-left font-bold uppercase text-[10px]">Region</th>
                    <th className="pb-2 text-right font-bold uppercase text-[10px]">7-Day Velocity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {macroData.trendingDemand.length === 0 ? (
                      <tr><td colSpan="3" className="py-4 text-gray-500 italic text-center">No trending RFQ data this week.</td></tr>
                  ) : (
                    macroData.trendingDemand.map((trend, idx) => (
                      <tr key={idx}>
                        <td className="py-3 font-bold text-slate-800">{trend.hs_code}</td>
                        <td className="py-3 text-slate-600">{trend.province}</td>
                        <td className="py-3 text-right"><span className={`${trend.velocity.startsWith('+') ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'} font-bold px-2 py-1 rounded`}>{trend.velocity}</span></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Supply Shortage Indicators</h4>
              <p className="text-xs text-slate-500 mb-4">HS Codes with high RFQ volume but &lt;1 quote response per request.</p>
              
              <div className="space-y-3">
                {macroData.shortages.map((shortage, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 border border-slate-100 rounded">
                    <span className="font-bold text-slate-800 block text-sm">HS: {shortage.hs_code}</span>
                    <span className="text-[10px] text-slate-500">{shortage.name}</span>
                    <div className="mt-2 text-xs font-mono text-red-600 font-bold">Response Rate: {shortage.rate}/RFQ</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}